import type { DayResponse } from "../../shared/types.js";

const WEEKDAY = [
  "Sunday", "Monday", "Tuesday", "Wednesday",
  "Thursday", "Friday", "Saturday",
];
const MONTH = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const SVG_NS = "http://www.w3.org/2000/svg";

function timeOfDayMinutes(iso: string): number {
  const [, tp] = iso.split("T");
  const [h, m] = (tp ?? "0:0").split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function formatTime(iso: string): string {
  const [, tp] = iso.split("T");
  const [h, m] = (tp ?? "0:0").split(":").map(Number);
  const hour = h ?? 0;
  const h12 = ((hour + 11) % 12) + 1;
  const am = hour < 12 ? "AM" : "PM";
  return `${h12}:${String(m ?? 0).padStart(2, "0")} ${am}`;
}

function formatLongDate(date: string): string {
  const [y, mo, d] = date.split("-").map(Number);
  const weekday = new Date(Date.UTC(y!, mo! - 1, d!)).getUTCDay();
  return `${WEEKDAY[weekday]}, ${MONTH[mo! - 1]} ${d}, ${y}`;
}

function formatHeight(h: number, digits = 2): string {
  return `${h >= 0 ? "+" : ""}${h.toFixed(digits)} ft`;
}

export function renderDayView(container: HTMLElement, data: DayResponse): void {
  container.replaceChildren();

  const nav = document.createElement("p");
  nav.className = "day-back";
  const backLink = document.createElement("a");
  backLink.href = "/";
  backLink.textContent = "← All low tides";
  nav.appendChild(backLink);
  container.appendChild(nav);

  const heading = document.createElement("h2");
  heading.className = "day-title";
  heading.textContent = formatLongDate(data.date);
  container.appendChild(heading);

  const subtitle = document.createElement("p");
  subtitle.className = "day-subtitle";
  subtitle.textContent = data.location.name;
  container.appendChild(subtitle);

  container.appendChild(renderSunInfo(data));
  container.appendChild(renderCurve(data));
  container.appendChild(renderEvents(data));
  if (data.scoredLows.length > 0) {
    container.appendChild(renderScoreBreakdown(data));
  }
}

function renderSunInfo(data: DayResponse): HTMLElement {
  const wrap = document.createElement("section");
  wrap.className = "sun-info";
  const items: ReadonlyArray<readonly [string, string]> = [
    ["Sunrise", data.sun.sunrise],
    ["Solar noon", data.sun.solarNoon],
    ["Sunset", data.sun.sunset],
  ];
  for (const [label, iso] of items) {
    const item = document.createElement("div");
    item.className = "sun-info-item";
    const lbl = document.createElement("span");
    lbl.className = "sun-info-label";
    lbl.textContent = label;
    const val = document.createElement("span");
    val.className = "sun-info-value";
    val.textContent = formatTime(iso);
    item.appendChild(lbl);
    item.appendChild(val);
    wrap.appendChild(item);
  }
  return wrap;
}

function renderCurve(data: DayResponse): HTMLElement {
  const wrap = document.createElement("section");
  wrap.className = "tide-curve";

  const heights = [
    ...data.hourly.map((p) => p.height),
    ...data.events.map((p) => p.height),
  ];
  if (heights.length === 0) {
    wrap.textContent = "No tide curve data available for this day.";
    return wrap;
  }

  const VB_W = 240;
  const VB_H = 130;
  const PLOT_X = 22;
  const PLOT_Y = 8;
  const PLOT_W = VB_W - PLOT_X - 6;
  const PLOT_H = 96;

  const minH = Math.min(...heights);
  const maxH = Math.max(...heights);
  const pad = Math.max(0.5, (maxH - minH) * 0.1);
  const yMin = Math.floor((minH - pad) * 2) / 2;
  const yMax = Math.ceil((maxH + pad) * 2) / 2;

  const xFromMin = (min: number): number => PLOT_X + (min / 1440) * PLOT_W;
  const yFromHeight = (h: number): number =>
    PLOT_Y + PLOT_H - ((h - yMin) / (yMax - yMin)) * PLOT_H;

  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${VB_W} ${VB_H}`);
  svg.setAttribute("preserveAspectRatio", "none");
  svg.setAttribute("class", "tide-curve-svg");
  svg.setAttribute("role", "img");
  svg.setAttribute("aria-label",
    `Tide curve for ${data.location.name} on ${data.date}`);

  const sunriseX = xFromMin(timeOfDayMinutes(data.sun.sunrise));
  const sunsetX = xFromMin(timeOfDayMinutes(data.sun.sunset));
  const band = document.createElementNS(SVG_NS, "rect");
  band.setAttribute("class", "tide-curve-daylight");
  band.setAttribute("x", sunriseX.toFixed(2));
  band.setAttribute("y", PLOT_Y.toString());
  band.setAttribute("width", (sunsetX - sunriseX).toFixed(2));
  band.setAttribute("height", PLOT_H.toString());
  svg.appendChild(band);

  const pts = data.hourly.map((p) => {
    return [xFromMin(timeOfDayMinutes(p.time)), yFromHeight(p.height)] as const;
  });
  if (pts.length > 1) {
    const firstX = pts[0]![0];
    const lastX = pts[pts.length - 1]![0];
    const baseY = PLOT_Y + PLOT_H;
    const areaD = [
      `M ${firstX.toFixed(2)} ${baseY}`,
      ...pts.map(([x, y]) => `L ${x.toFixed(2)} ${y.toFixed(2)}`),
      `L ${lastX.toFixed(2)} ${baseY}`,
      "Z",
    ].join(" ");
    const area = document.createElementNS(SVG_NS, "path");
    area.setAttribute("class", "tide-curve-area");
    area.setAttribute("d", areaD);
    svg.appendChild(area);

    const lineD = pts
      .map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`)
      .join(" ");
    const line = document.createElementNS(SVG_NS, "path");
    line.setAttribute("class", "tide-curve-line");
    line.setAttribute("d", lineD);
    svg.appendChild(line);
  }

  for (const hour of [0, 6, 12, 18]) {
    const x = xFromMin(hour * 60);
    const tick = document.createElementNS(SVG_NS, "text");
    tick.setAttribute("class", "tide-curve-tick");
    tick.setAttribute("x", x.toFixed(2));
    tick.setAttribute("y", (PLOT_Y + PLOT_H + 12).toString());
    tick.setAttribute("text-anchor", "middle");
    const h12 = ((hour + 11) % 12) + 1;
    const am = hour < 12 ? "a" : "p";
    tick.textContent = hour === 0 ? "12a" : `${h12}${am}`;
    svg.appendChild(tick);
  }
  for (const h of [yMin, (yMin + yMax) / 2, yMax]) {
    const y = yFromHeight(h);
    const tick = document.createElementNS(SVG_NS, "text");
    tick.setAttribute("class", "tide-curve-tick");
    tick.setAttribute("x", (PLOT_X - 4).toString());
    tick.setAttribute("y", (y + 3).toFixed(2));
    tick.setAttribute("text-anchor", "end");
    tick.textContent = h.toFixed(1);
    svg.appendChild(tick);
  }

  for (const ev of data.events) {
    const x = xFromMin(timeOfDayMinutes(ev.time));
    const y = yFromHeight(ev.height);
    const dot = document.createElementNS(SVG_NS, "circle");
    dot.setAttribute("class",
      `tide-curve-event tide-curve-event-${ev.tideType.toLowerCase()}`);
    dot.setAttribute("cx", x.toFixed(2));
    dot.setAttribute("cy", y.toFixed(2));
    dot.setAttribute("r", "2.5");
    svg.appendChild(dot);

    const labelY = ev.tideType === "H" ? y - 5 : y + 10;
    const label = document.createElementNS(SVG_NS, "text");
    label.setAttribute("class", "tide-curve-event-label");
    label.setAttribute("x", x.toFixed(2));
    label.setAttribute("y", labelY.toFixed(2));
    label.setAttribute("text-anchor", "middle");
    label.textContent = `${ev.height >= 0 ? "+" : ""}${ev.height.toFixed(1)}`;
    svg.appendChild(label);
  }

  wrap.appendChild(svg);
  return wrap;
}

function renderEvents(data: DayResponse): HTMLElement {
  const wrap = document.createElement("section");
  wrap.className = "day-events";

  const heading = document.createElement("h3");
  heading.textContent = "Tides today";
  wrap.appendChild(heading);

  const table = document.createElement("table");
  const thead = document.createElement("thead");
  const headRow = document.createElement("tr");
  for (const label of ["Type", "Time", "Height"]) {
    const th = document.createElement("th");
    th.scope = "col";
    th.textContent = label;
    headRow.appendChild(th);
  }
  thead.appendChild(headRow);
  table.appendChild(thead);

  const tbody = document.createElement("tbody");
  for (const ev of data.events) {
    const tr = document.createElement("tr");
    if (ev.tideType === "L" && ev.height < 0) tr.className = "row-negative";

    const typeCell = document.createElement("td");
    typeCell.textContent = ev.tideType === "H" ? "High" : "Low";
    tr.appendChild(typeCell);

    const timeCell = document.createElement("td");
    timeCell.textContent = formatTime(ev.time);
    tr.appendChild(timeCell);

    const heightCell = document.createElement("td");
    heightCell.textContent = formatHeight(ev.height);
    if (ev.height < 0) heightCell.className = "height-negative";
    tr.appendChild(heightCell);

    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  wrap.appendChild(table);
  return wrap;
}

function renderScoreBreakdown(data: DayResponse): HTMLElement {
  const wrap = document.createElement("section");
  wrap.className = "score-breakdown";

  const heading = document.createElement("h3");
  heading.textContent = "Tidepooling score";
  wrap.appendChild(heading);

  for (const low of data.scoredLows) {
    const item = document.createElement("div");
    item.className = "score-breakdown-item";

    const header = document.createElement("div");
    header.className = "score-breakdown-header";
    const title = document.createElement("span");
    title.className = "score-breakdown-time";
    title.textContent = `Low at ${formatTime(low.time)} (${formatHeight(low.height)})`;
    if (!low.inDaylight) {
      const tag = document.createElement("span");
      tag.className = "tag tag-outside";
      tag.textContent = "Outside daylight";
      title.appendChild(document.createTextNode(" "));
      title.appendChild(tag);
    }
    const total = document.createElement("span");
    total.className = "score-breakdown-total";
    total.textContent = `${low.score.toFixed(0)} / 100`;
    header.appendChild(title);
    header.appendChild(total);
    item.appendChild(header);

    const list = document.createElement("ul");
    list.className = "score-breakdown-list";
    const rows: ReadonlyArray<readonly [string, number, number, string]> = [
      ["Height", low.components.heightScore, 70, formatHeight(low.height)],
      ["Time of day", low.components.timeScore, 10,
        `${low.minutesFromSolarNoon} min from solar noon`],
      ["Weekend", low.components.weekendScore, 20, low.isWeekend ? "Weekend" : "Weekday"],
    ];
    for (const [label, value, max, note] of rows) {
      const li = document.createElement("li");

      const lbl = document.createElement("span");
      lbl.className = "score-breakdown-label";
      lbl.textContent = label;
      li.appendChild(lbl);

      const bar = document.createElement("span");
      bar.className = "score-breakdown-bar";
      const fill = document.createElement("span");
      fill.className = "score-breakdown-bar-fill";
      const pct = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
      fill.style.width = `${(pct * 100).toFixed(0)}%`;
      bar.appendChild(fill);
      li.appendChild(bar);

      const val = document.createElement("span");
      val.className = "score-breakdown-value";
      val.textContent = `${value.toFixed(1)} / ${max}`;
      li.appendChild(val);

      const noteEl = document.createElement("span");
      noteEl.className = "score-breakdown-note";
      noteEl.textContent = note;
      li.appendChild(noteEl);

      list.appendChild(li);
    }
    item.appendChild(list);
    wrap.appendChild(item);
  }

  return wrap;
}
